<div class="protocol-tomass">
  <div class="protocol-tomass__header">
    <app-details-header
      title="TOMASS｜咀嚼嚥下機能チェック"
      i18n-title="@@details.protocolTomass.headerTitle"
      badgeText="スクリーニング"
      i18n-badgeText="@@details.protocolTomass.headerBadge"
      [patientName]="patientName()"
      [measureDate]="measureDate()"
      [measurer]="measurer()" />
  </div>

  <div class="protocol-tomass__body">
    <!-- Warning Banner (C only) -->
    @if (isWarning()) {
      <div class="warning-banner">
        <mat-icon class="warning-banner__icon">warning_amber</mat-icon>
        <div class="warning-banner__content">
          <span class="warning-banner__title" i18n="@@details.protocolTomass.warningTitle">咀嚼嚥下機能の低下を検出</span>
          <span class="warning-banner__desc" i18n="@@details.protocolTomass.warningDesc">咀嚼嚥下機能チェックで要注意の所見が検出されました。専門医への相談を推奨します。</span>
        </div>
      </div>
    }

    <!-- Hint Banner (A only) -->
    @if (isGood()) {
      <div class="hint-banner">
        <mat-icon class="hint-banner__icon">restaurant</mat-icon>
        <span class="hint-banner__text" i18n="@@details.protocolTomass.hintFastEating">早食い傾向の可能性があります。ゆっくりよく噛んで食べることを心がけましょう。</span>
      </div>
    }

    <!-- KPI Row: Score + Adopted Result -->
    <div class="kpi-row kpi-row--half">
      <!-- Score Card -->
      <div class="kpi-card">
        <span class="kpi-card__title" i18n="@@details.protocolTomass.kpiScoreTitle">総合判定</span>
        <div class="kpi-card__main">
          <div class="result-indicator"
            [class.result-indicator--ok]="isGood()"
            [class.result-indicator--warn]="isWarning()">
            <div class="score-circle"
              [class.score-circle--a]="isGood()"
              [class.score-circle--b]="scoreValue() === 'B'"
              [class.score-circle--c]="isWarning()">
              {{ scoreValue() }}
            </div>
            <div class="result-indicator__text">
              <span class="result-indicator__label">{{ scoreLabel() }}</span>
              <span class="result-indicator__desc">{{ resultLabel() }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Adopted Result Card -->
      <div class="kpi-card">
        <span class="kpi-card__title" i18n="@@details.protocolTomass.kpiAdoptedTitle">採用計測値</span>
        <div class="kpi-card__main">
          <div class="adopted-result">
            <span class="adopted-result__value">{{ adoptedDisplay() }}</span>
            @if (adoptedSource()) {
              <span class="adopted-result__source">{{ adoptedLabel() }}</span>
            }
          </div>
        </div>
      </div>
    </div>

    <!-- Measurement Detail (accordion) -->
    <div class="detail-card">
      <button
        type="button"
        class="detail-card__header"
        [attr.aria-expanded]="detailExpanded()"
        aria-controls="tomass-detail-body"
        (click)="toggleDetail()">
        <div class="detail-card__header-left">
          <mat-icon class="detail-card__icon">assignment</mat-icon>
          <span class="detail-card__title" i18n="@@details.protocolTomass.detailTitle">計測詳細</span>
        </div>
        <mat-icon class="detail-card__chevron" [class.detail-card__chevron--open]="detailExpanded()">
          expand_more
        </mat-icon>
      </button>

      @if (detailExpanded()) {
        <div id="tomass-detail-body" class="detail-card__body">
          <div class="detail-grid">
            <div class="detail-grid__item">
              <span class="detail-grid__label" i18n="@@details.protocolTomass.ageGroupLabel">年齢層</span>
              <span class="detail-grid__value">{{ ageGroupDisplay() }}</span>
            </div>
            <div class="detail-grid__item">
              <span class="detail-grid__label" i18n="@@details.protocolTomass.pstDetailLabel">自己申告</span>
              <div class="detail-grid__value-row">
                <span class="detail-grid__value">{{ pstDisplay() }}</span>
                @if (adoptedSource() === 'pst') {
                  <span class="adopted-chip" i18n="@@details.protocolTomass.adoptedChip">採用</span>
                }
              </div>
            </div>
            <div class="detail-grid__item">
              <span class="detail-grid__label" i18n="@@details.protocolTomass.gpstDetailLabel">デバイス検出</span>
              <div class="detail-grid__value-row">
                <span class="detail-grid__value">{{ gpstDisplay() }}</span>
                @if (adoptedSource() === 'gpst') {
                  <span class="adopted-chip" i18n="@@details.protocolTomass.adoptedChip">採用</span>
                }
              </div>
            </div>
            @if (hasGeolocation()) {
              <div class="detail-grid__item detail-grid__item--full">
                <span class="detail-grid__label">
                  <mat-icon class="detail-grid__icon">location_on</mat-icon>
                  <span i18n="@@details.protocolTomass.locationLabel">計測地域</span>
                </span>
                <div class="detail-grid__map-row">
                  <div class="detail-grid__map" #tomassMap aria-label="計測地域 地図"></div>
                </div>
              </div>
            }
          </div>
        </div>
      }
    </div>

    <!-- Wave + Posture + Event Timeline Card -->
    @if (showWaveCard()) {
      <div class="timeline-card">
        <div class="timeline-card__header">
          <div class="timeline-card__header-left">
            <mat-icon class="timeline-card__icon">graphic_eq</mat-icon>
            <span class="timeline-card__title">
              <span class="timeline-card__title-main" i18n="@@details.protocolTomass.timelineTitle">波形タイムライン</span>
              <span class="timeline-card__title-detail" i18n="@@details.protocolTomass.timelineSubtitle"> — 波形・姿勢・イベント</span>
            </span>
          </div>
          <span class="timeline-card__duration">
            <span i18n="@@details.protocolTomass.recordingDurationLabel">録音時間: </span>{{ recordData()?.data?.totalTime ?? '' }}
          </span>
        </div>

        <div class="timeline-card__grid">
          <div class="timeline-card__wave">
            <app-measured-data-wave-card
              [frameless]="true"
              [showMiniPlayback]="true"
              [recordData]="recordData()"
              [recordtype]="recordtype()"
              [recordLoadingSuccess]="recordLoadingSuccess()"
              [waveLoadingPercent]="waveLoadingPercent()"
              [waveErrorMessage]="waveErrorMessage()"
              [selectedEvent]="selectedEvent()"
              [currentStartOffset]="currentStartOffset()"
              [playing]="playing()"
              [timelineOffsetReference]="timelineOffsetReference()"
              [zoomValue]="zoomValue()"
              (offsetToggle)="offsetToggle.emit($event)"
              (seek)="seek.emit($event)"
              (playPauseClicked)="playPauseClicked.emit()"
              (zoomMinusClicked)="zoomMinusClicked.emit()"
              (zoomResetClicked)="zoomResetClicked.emit()"
              (zoomPlusClicked)="zoomPlusClicked.emit()" />
          </div>

          <div class="timeline-card__posture">
            <app-measured-data-neck-angle-card
              [frameless]="true"
              [chartOption]="chartOption()"
              [currentChartOption]="currentChartOption()"
              [currentOrientation]="currentOrientation()"
              (chartInit)="chartInit.emit($event)"
              (currentChartInit)="currentChartInit.emit($event)" />
          </div>

          <div class="timeline-card__events">
            <app-event-table
              [events]="events()"
              (seekToEvent)="onEventSeek($event)" />
          </div>

          <div class="timeline-card__footer">
            <app-data-footer
              [dataId]="dataId()"
              nbModel="nbModel v2.1"
              [downloadUrl]="downloadUrl()" />
          </div>
        </div>
      </div>
    }

    <!-- Reference Card -->
    <app-reference-card [searchLinks]="searchLinks()" />

    <!-- Disclaimer -->
    <app-disclaimer [message]="disclaimer" />
  </div>
</div>
